import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Transaction } from "../../../../model/transaction";
import {
	useGetPrepaidTransactionByReferenceForUtilityAdminQuery,
	useGetPostpaidTransactionByReferenceForUtilityAdminQuery,
} from "../../../../service/transaction-service";
import { useReVendMutation, useVerifyVendingTokenMutation, useNotifyBillingMutation } from "../../../../service/vending-service";
import { useLazyVerifyTransactionQuery } from "../../../../service/charge-service";
import { apiSlice } from "../../../../stores/base/api-slice";
import { formatCurrency } from "../../../../utils";
import { getErrorMessage } from "../../../../utils/error-handler";
import TransactionDetails from "./TransactionDetails";
import { mapNotifyTimeline, mapTokenTimeline } from "./timeline";
import { TransactionDetailData, TransactionDetailType } from "./types";

interface TransactionDetailsContainerProps {
	reference: string;
	transactionType: TransactionDetailType;
	onClose: () => void;
}

const mapVending = (t?: Transaction): TransactionDetailData => ({
	date: t?.createdAt ?? "",
	time: t?.createdAt ?? "",
	customerName: `${t?.user.firstName ?? ""} ${t?.user.lastName ?? ""}`.trim(),
	amount: t?.amount?.amount ? formatCurrency(t.amount.amount, { currency: t.amount.currency }) : "N/A",
	paymentStatus: t?.status ?? 0,
	transactionId: t?.externalReference ?? "",
	unitBought: t?.vendingDetails?.vendingToken?.totalKwh?.toString() ?? "0",
	paymentChannel: t?.type ?? 0,
	token: t?.vendingDetails?.vendingToken?.token ?? "",
	mapRefundToken: t?.vendingDetails?.vendingToken?.mapRefundToken,
	vendingStatus: t?.vendingDetails?.vendingToken?.vendingStatus,
	reference: t?.reference ?? "",
	meterNumber: t?.vendingDetails?.meterNumber ?? "",
	address: t?.vendingDetails?.address ?? "",
	phoneNumber: t?.user?.phoneNumber ?? "",
	district: t?.vendingDetails?.district ?? "N/A",
	attempts: t?.vendingDetails?.totalTokenCount ?? 0,
	failed: t?.vendingDetails?.failedTokenCount ?? 0,
	remoteTokenLoadReference: t?.vendingDetails?.vendingToken?.vendingRemoteTokenLoadId,
});

const mapBilling = (t?: Transaction): TransactionDetailData => {
	const notifyAttempts = t?.billingDetails?.notifyTimeline ?? [];

	return {
		date: t?.billingDetails?.createdAt ?? "",
		time: t?.billingDetails?.createdAt ?? "",
		customerName: `${t?.user.firstName ?? ""} ${t?.user.lastName ?? ""}`.trim(),
		amount: t?.amount?.amount ? formatCurrency(t.amount.amount, { currency: t.amount.currency }) : "N/A",
		paymentStatus: t?.status ?? 0,
		transactionId: t?.externalReference ?? "",
		paymentChannel: t?.type ?? 0,
		token: t?.externalReference ?? "",
		billingStatus: t?.billingDetails?.isBillingNotified,
		reference: t?.reference ?? "",
		meterNumber: t?.billingDetails?.meterNumber ?? "",
		accountNumber: t?.billingDetails?.accountNumber ?? "",
		address: t?.billingDetails?.address ?? "",
		outstandingBalance: t?.billingDetails?.previousOutstandingBalance
			? formatCurrency(t.billingDetails.previousOutstandingBalance.amount, { currency: t.billingDetails.previousOutstandingBalance.currency })
			: "N/A",
		phoneNumber: t?.user?.phoneNumber ?? "",
		district: t?.billingDetails?.district ?? "N/A",
		attempts: notifyAttempts.length,
		failed: notifyAttempts.filter(a => !a.isSuccess && !!a.errorMessage).length,
		lastAttemptReference: notifyAttempts[notifyAttempts.length - 1]?.id,
	};
};

const TransactionDetailsContainer = ({ reference, transactionType, onClose }: TransactionDetailsContainerProps) => {
	const dispatch = useDispatch();
	const isVending = transactionType === "vending";

	const vendingQuery = useGetPrepaidTransactionByReferenceForUtilityAdminQuery(
		{ Reference: reference },
		{ skip: !isVending || !reference, refetchOnMountOrArgChange: true }
	);
	const billingQuery = useGetPostpaidTransactionByReferenceForUtilityAdminQuery(
		{ Reference: reference },
		{ skip: isVending || !reference, refetchOnMountOrArgChange: true }
	);
	const { data: transaction, isFetching, isError } = isVending ? vendingQuery : billingQuery;

	const [revend, { isLoading: reVending }] = useReVendMutation();
	const [verifyVendingToken, { isLoading: verifyingVendingToken }] = useVerifyVendingTokenMutation();
	const [notifyBilling, { isLoading: notifyingBilling }] = useNotifyBillingMutation();
	const [verifyPayment, { isLoading: verifyingPayment }] = useLazyVerifyTransactionQuery();

	const handleRevend = () => {
		revend({ vendingDetailsId: transaction?.vendingDetails?.id ?? "" })
			.unwrap()
			.then(() => toast.success("Vending Successful"))
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const handleVerifyVendingToken = () => {
		verifyVendingToken(transaction?.vendingDetails?.id ?? "")
			.unwrap()
			.then(() => toast.success("Vending Successful"))
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const handleNotifyBilling = () => {
		notifyBilling({ billingDetailId: transaction?.billingDetails?.id ?? "" })
			.unwrap()
			.then(() => toast.success("Billing notified successfully"))
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const handleVerifyPaymentStatus = () => {
		verifyPayment({ reference })
			.unwrap()
			.then(response => {
				if (response.status === 2 || response.status === 3) {
					toast.success("Payment status updated, fetching updated transaction details...");
					// verifyTransaction has no tags of its own, so refresh the detail + list manually.
					dispatch(apiSlice.util.invalidateTags(["Transaction", "Transactions"]));
				} else {
					toast.info("Payment status is still in progress");
				}
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const data = isVending ? mapVending(transaction) : mapBilling(transaction);

	return (
		<TransactionDetails
			transactionType={transactionType}
			transaction={data}
			timeline={isVending ? mapTokenTimeline(transaction?.vendingDetails?.tokenTimeline) : mapNotifyTimeline(transaction?.billingDetails?.notifyTimeline)}
			onClose={onClose}
			onRevend={handleRevend}
			reVendLoading={reVending}
			onVerifyVendingToken={handleVerifyVendingToken}
			verifyingVendingToken={verifyingVendingToken}
			onNotifyBilling={handleNotifyBilling}
			notifyBillingLoading={notifyingBilling}
			onVerifyPaymentStatus={handleVerifyPaymentStatus}
			verifyingPayment={verifyingPayment}
			isLoadingTransaction={isFetching}
			isError={isError}
		/>
	);
};

export default TransactionDetailsContainer;
